// src/pages/agent/MySubmissions.js
// Lead-submitter view: shows leads they've submitted (via collectionGroup
// query filtered by submittedBy.uid). Read-only, minimal columns.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collectionGroup, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAgentAuth } from '../../lib/auth';
import { PageHeader, Card, Btn, BORDER, BURG, BURG_DK, MID, LIGHT, StatusPill, EmptyState } from '../../components/agent/UI';

export default function MySubmissions() {
  const { profile } = useAgentAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.uid) return;
    const q = query(
      collectionGroup(db, 'leads'),
      where('submittedBy.uid', '==', profile.uid),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, snap => {
      setLeads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, err => { console.error(err); setLoading(false); });
    return unsub;
  }, [profile?.uid]);

  return (
    <>
      <PageHeader
        eyebrow="Your activity"
        title="My submissions"
        sub="Every lead you've submitted. Status updates as your admin works the lead through the pipeline."
        right={<Btn variant="gold" onClick={() => navigate('/agent/submit')}>+ Submit a Lead</Btn>}
      />

      <Card padded={false}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: LIGHT }}>Loading…</div>
        ) : leads.length === 0 ? (
          <EmptyState
            title="No submissions yet"
            sub="Click the button to submit your first lead. Your admin will see it in their pipeline immediately."
            action={<Btn variant="gold" onClick={() => navigate('/agent/submit')}>+ Submit your first lead</Btn>}
          />
        ) : (
          <table style={ss.table}>
            <thead>
              <tr>
                <th style={ss.th}>Property</th>
                <th style={ss.th}>Owner</th>
                <th style={ss.th}>Phone</th>
                <th style={ss.th}>Status</th>
                <th style={ss.th}>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(l => (
                <tr
                  key={l.id}
                  onClick={() => navigate(`/agent/submissions/${l.id}`)}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#FAF7F2'}
                  onMouseLeave={(e) => e.currentTarget.style.background = ''}
                >
                  <td style={ss.td}>
                    <div style={{ fontWeight: 500, color: BURG_DK }}>{l.address?.line1 || '(no address)'}</div>
                    <div style={{ fontSize: 11, color: LIGHT }}>
                      {[l.address?.city, l.address?.state, l.address?.zip].filter(Boolean).join(', ')}
                    </div>
                  </td>
                  <td style={ss.td}>{(l.owners || []).map(o => o.name).filter(Boolean).join(', ') || '—'}</td>
                  <td style={{ ...ss.td, fontFamily: 'monospace', fontSize: 12 }}>{(l.owners || [])[0]?.phone || '—'}</td>
                  <td style={ss.td}><StatusPill status={l.status} /></td>
                  <td style={{ ...ss.td, fontSize: 11, color: LIGHT }}>{l.createdAt?.toDate?.()?.toLocaleDateString?.() || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

const ss = {
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '12px 18px', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: LIGHT, borderBottom: `1px solid ${BORDER}` },
  td: { padding: '14px 18px', fontSize: 13, borderBottom: `1px solid ${BORDER}`, color: '#1C1C1C', verticalAlign: 'top' }
};
